import React from "react";

import { CheckCircle2, Circle, Clock, XCircle } from "lucide-react";

import { FLOW } from "./flowCopy";
import { planFlow } from "./planFlow";
import type { AccountPlanOverview, PlanFile } from "../../Types/AccountPlan";

type PartState = "ready" | "pending" | "failed" | "waiting";

export interface BriefingPart {
  key: "briefing" | "questions" | "chat";
  label: string;
  state: PartState;
  detail: string;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/**
 * Who Ask about this plan answers from, for the version under review. Since
 * 2026-11-01 (product review) chat answers only from a PUBLISHED version, for the
 * team and the sponsor alike: before the first publish it waits; while a newer
 * draft is in review it stays on the published one.
 */
export const askAboutPlanLine = (
  data: AccountPlanOverview,
  file: PlanFile
): { live: boolean; text: string } => {
  // The plan number only (D1); `version` is internal and never shown.
  const vN =
    file.plan_version != null ? `v${file.plan_version}` : "this version";
  const published = planFlow(data).published;
  if (published && published.fileId === file.id)
    return {
      live: true,
      text: `Your team and the sponsor can ask about ${vN}.`,
    };
  if (published)
    return {
      live: true,
      text: `Answers from v${published.planVersion} until you publish ${vN}.`,
    };
  return {
    live: false,
    text: `Opens when you publish ${vN}, for your team and the sponsor.`,
  };
};

/**
 * The three things one briefing run produces, with the same status each (Sushant,
 * 2026-10-31): the briefing, its Briefing Questions answers, and Ask about this plan.
 */
export const briefingStatusParts = (
  data: AccountPlanOverview,
  file: PlanFile
): BriefingPart[] => {
  const ready = file.sponsor_view_status === "ready";
  const failed = file.sponsor_view_status === "failed";
  const view = file.sponsor_view;
  const sections = file.progress?.briefing?.steps?.write?.items?.length ?? 0;
  const answers = view?.briefing?.answers?.length ?? 0;
  const qsFailed = ready && view?.briefing_status === "failed";
  const ask = askAboutPlanLine(data, file);
  return [
    {
      key: "briefing",
      label: FLOW.briefingPart,
      state: ready ? "ready" : failed ? "failed" : "pending",
      detail: ready
        ? sections
          ? `${plural(sections, "section")} written and checked against the slides`
          : "Written and checked against the slides"
        : failed
          ? "Couldn't be written"
          : "Being written",
    },
    {
      key: "questions",
      label: FLOW.briefingQuestions,
      state: qsFailed
        ? "failed"
        : ready
          ? "ready"
          : failed
            ? "failed"
            : "pending",
      detail: qsFailed
        ? "Couldn't be answered for this draft"
        : ready
          ? answers
            ? `${plural(answers, "question")} answered from the plan`
            : "Answered from the plan"
          : "Being answered",
    },
    {
      key: "chat",
      label: FLOW.askAboutPlan,
      state: ready
        ? ask.live
          ? "ready"
          : "waiting"
        : failed
          ? "failed"
          : "pending",
      detail: ready ? ask.text : "Opens once the briefing is published",
    },
  ];
};

interface Props {
  data: AccountPlanOverview;
  file: PlanFile;
}

/** Review briefing: what this run produced, side by side, once the briefing is ready. */
const BriefingStatus: React.FC<Props> = ({ data, file }) => (
  <ul
    data-testid="briefing-parts"
    aria-label="What this briefing includes"
    className="grid gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm md:grid-cols-3"
  >
    {briefingStatusParts(data, file).map(p => (
      <li
        key={p.key}
        data-testid={`briefing-part-${p.key}`}
        data-state={p.state}
        className="flex min-w-0 items-start gap-2"
      >
        {p.state === "ready" ? (
          <CheckCircle2
            className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600"
            aria-label="ready"
          />
        ) : p.state === "waiting" ? (
          <Clock
            className="mt-0.5 h-4 w-4 shrink-0 text-gray-400"
            aria-label="waiting"
          />
        ) : p.state === "failed" ? (
          <XCircle
            className="mt-0.5 h-4 w-4 shrink-0 text-red-500"
            aria-label="failed"
          />
        ) : (
          <Circle
            className="mt-0.5 h-4 w-4 shrink-0 text-gray-300"
            aria-label="pending"
          />
        )}
        <div className="min-w-0">
          <div className="font-medium text-gray-900">{p.label}</div>
          <div className="text-xs text-gray-600">{p.detail}</div>
        </div>
      </li>
    ))}
  </ul>
);

export default BriefingStatus;
